<template>
    <card cardBodyClasses="p-0">
        <b-row>
            <b-col cols="10">
                <h4 class="card-subtitle" style="padding-top: 15px; padding-left: 15px">
                    Voicemails
                </h4>
            </b-col>
        </b-row>

        <template v-if="hasVoicemailPermission">
            <div v-if="loading" class="text-center p-3 d-flex align-items-center justify-content-center"
                style="gap: 6px;">
                <b-spinner small />
                Loading voicemails...
            </div>

            <div v-else-if="!voicemails.length" class="text-center p-2">
                No Voicemails On File
            </div>

            <div class="p-2 pb-0" v-else>
                <b-table-simple bordered class="mb-0">
                    <tbody>
                        <tr v-for="item in voicemails" :key="`${item.id}`">
                            <td>
                                <div style="cursor:pointer" @click="toggleInfo(item)">
                                    <p class="mb-0">{{ item.id }}: Received {{ relativeTime(item) }}</p>
                                </div>
                                <b-collapse :id="`accordion-${item.id}`" accordion="patient-voicemail-accordion"
                                    role="tabpanel">
                                    <div class="ml-3 mt-2">
                                        <b-row v-if="item?.phoneNumber">
                                            <b-col>
                                                <p><strong>Phone Number:</strong> {{ item.phoneNumber | formatPhone }}</p>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                <h6 style="font-weight: 500">
                                                    Transcription
                                                    <i class="fas fa-download" style="color: blue; cursor:pointer"
                                                        @click="downloadVoicemail(item.id)"></i>
                                                </h6>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                <audio :id="`audio-${item.id}`" controls hidden>
                                                    <source :id="`source-${item.id}`" type="audio/wav" />
                                                </audio>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col>
                                                <p>{{ item.transcript }}</p>
                                            </b-col>
                                        </b-row>
                                        <b-row v-if="item.completed">
                                            <b-col>
                                                <p class="mb-0"><strong>Completed:</strong> {{
                                                    formatDate(item.completed) }}
                                                    by {{
                                                        item.completedBy
                                                    }}</p>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </td>
                        </tr>
                    </tbody>
                </b-table-simple>
            </div>
        </template>

        <b-alert :show="!hasVoicemailPermission" variant="warning" class="my-2" style="margin: 15px;">
            <strong>
                You do not have permissions to view voicemails.
            </strong>
        </b-alert>
    </card>
</template>

<script lang="ts">

import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import { Voicemail } from "@/models/Voicemail";
import axios, { AxiosRequestConfig } from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "PatientVoiceMailComponent",
})
export default class PatientVoiceMailComponent extends Vue {
    @Prop() private patient!: Patient;
    private voicemails: Voicemail[] = [];
    private loading = false;

    get hasVoicemailPermission(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Services/Voicemails');
    }

    mounted() {
        if (this.patient?.id && this.hasVoicemailPermission) {
            this.getPatientVoiceMails();
        }
    }

    @Watch('patient')
    onPatientChanged(newPatient: Patient, oldPatient: Patient) {
        if (newPatient?.id !== oldPatient?.id && newPatient?.id && this.hasVoicemailPermission) {
            this.voicemails = [];
            this.getPatientVoiceMails();
        }
    }

    async getPatientVoiceMails(): Promise<void> {
        if (!this.patient?.id) return;
        const completedFilterModeId = 1;
        try {
            this.loading = true;
            const response = await axios.get<Voicemail[]>(`/Voicemail/patient/${this.patient.id}/${completedFilterModeId}`);
            this.voicemails = response.data?.map(value => new Voicemail(value)) ?? [];
        } catch (error) {
            console.error('Error fetching patient voicemails:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    relativeTime(item: Voicemail): string {
        const voicemailReceivedTime = moment.utc(item.received);
        return `${voicemailReceivedTime.fromNow()} (${voicemailReceivedTime.local().format("L LT")})`;
    }

    formatDate(date: Date | undefined): string {
        if (!date) return '';
        return moment(date).local().format("L LT");
    }

    toggleInfo(item: Voicemail) {
        const idToExpand = `accordion-${item.id}`;
        this.$root.$emit("bv::toggle::collapse", idToExpand);
    }

    async downloadVoicemail(itemId: number) {
        try {
            const config: AxiosRequestConfig = {
                responseType: 'blob'
            }
            const response = await axios.get(`/api/Voicemail/DownloadAudio/${itemId}`, config);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "audio/wav" }));
            const sourceItem = document.getElementById(`source-${itemId}`) as HTMLSourceElement;
            sourceItem.src = url;
            const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;
            audioItem.hidden = false;
            audioItem.pause();
            audioItem.load();
            audioItem.play();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }
}
</script>

<style scoped></style>
